import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/Button';
import { Input } from '@components/forms/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/Card';
import { AnonymousHeader } from '@components/layout/AnonymousHeader';
import { AnonymousFooter } from '@components/layout/AnonymousFooter';
import { useConsent } from '@contexts/useConsent';
import { trackPurchase } from '@utils/marketing/metaPixel';
import { useAuth } from './useAuth';
import { authService } from './authService';
import { LoginResponse } from './types';

interface RegistrationPayment {
  organizationId: string;
  value?: number;
}

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { login } = useAuth();
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [info, setInfo] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { marketingActive } = useConsent();
  const paymentRef = useRef<RegistrationPayment | null>(null);
  const paymentReadRef = useRef(false);

  // Stripe returns here after the Basic registration payment
  // (?registration_paid=<org_id>&value=<net amount>). The parameters are
  // removed, so a reload does not count the payment again.
  useEffect(() => {
    if (paymentReadRef.current) return;
    paymentReadRef.current = true;
    const organizationId = searchParams.get('registration_paid');
    if (!organizationId) return;
    const rawValue = searchParams.get('value');
    paymentRef.current = {
      organizationId,
      value: rawValue ? Number(rawValue) : undefined,
    };
    setInfo(t('auth.registrationPaid'));
    const rest = new URLSearchParams(searchParams);
    rest.delete('registration_paid');
    rest.delete('value');
    setSearchParams(rest, { replace: true, state: location.state });
  }, [searchParams, setSearchParams, location.state, t]);

  // The Pixel loads after the consent provider's effect, or later when the
  // visitor accepts on this page.
  useEffect(() => {
    const payment = paymentRef.current;
    if (!payment || !marketingActive) return;
    paymentRef.current = null;
    // Only Basic pays at registration; postpaid tiers sign a SEPA mandate.
    trackPurchase(payment.organizationId, 'basic', payment.value);
  }, [marketingActive]);

  const handleInputChange = (field: keyof LoginFormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const data: LoginResponse = await authService.login({
        email: formData.email,
        password: formData.password,
      });

      // Check if payment is required (organization not active)
      if (data.payment_required) {
        // Don't authenticate the user - organization is not active
        // If checkout URL is provided, redirect to Stripe checkout
        if (data.checkout_url) {
          // Redirect to Stripe checkout page to complete payment
          window.location.href = data.checkout_url;
          return;
        }

        // Invoice-billed tiers are activated by the SEPA mandate, not by a payment.
        if (data.billing_mode === 'invoice') {
          setError(t('auth.registrationIncompleteSepa'));
          return;
        }

        // Fallback: Show error message if no checkout URL available
        const errorMessage =
          data.message ||
          data.error ||
          'Your organization is not active. Please complete the initial payment to activate your account.';
        setError(errorMessage);
        // Don't redirect - show error on login page
        return;
      }

      // Use auth context to store user data and tokens (only if payment not required)
      login(data.user, data.access, data.refresh);

      if (formData.rememberMe) {
        localStorage.setItem('remember_me', 'true');
      }

      // Redirect to intended page or dashboard
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.loginError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    // Navigate to forgot password page
    navigate('/forgot-password');
  };

  const handleCreateAccount = () => {
    navigate('/register');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 via-secondary-500 to-primary-600 flex flex-col login-page">
      {/* Anonymous Header */}
      <AnonymousHeader />

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardHeader className="text-center pb-8">
            <div className="mx-auto mb-6 w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
              <span className="material-icons text-2xl text-white">phone</span>
            </div>
            <CardTitle className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('auth.loginTitle')}
            </CardTitle>
            <CardDescription className="text-lg text-gray-700 dark:text-gray-400">
              {t('auth.loginSubtitle')}
            </CardDescription>
          </CardHeader>

          <CardContent className="px-8 pb-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Registration payment received */}
              {info && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <p className="text-sm text-green-700 dark:text-green-400">{info}</p>
                </div>
              )}

              {/* Email Input */}
              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-900 dark:text-gray-300"
                >
                  {t('auth.email')} *
                </label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={e => handleInputChange('email', e.target.value)}
                  placeholder={t('auth.email')}
                  required
                  className="w-full"
                  disabled={isLoading}
                />
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-gray-900 dark:text-gray-300"
                >
                  {t('auth.password')} *
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={e => handleInputChange('password', e.target.value)}
                    placeholder={t('auth.password')}
                    required
                    className="w-full pr-10"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    disabled={isLoading}
                  >
                    <span className="material-icons text-lg">
                      {showPassword ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="rememberMe"
                    type="checkbox"
                    checked={formData.rememberMe}
                    onChange={e => handleInputChange('rememberMe', e.target.checked)}
                    disabled={isLoading}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="rememberMe"
                    className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                  >
                    {t('common.rememberMe')}
                  </label>
                </div>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                  disabled={isLoading}
                >
                  {t('auth.forgotPassword')}
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {/* Sign In Button */}
              <Button
                type="submit"
                variant="gradient-multi"
                size="lg"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('common.loading')}
                  </div>
                ) : (
                  t('auth.signIn')
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">
                  {t('common.or')}
                </span>
              </div>
            </div>

            {/* Create Account Button */}
            <Button
              variant="outline"
              size="lg"
              className="w-full"
              onClick={handleCreateAccount}
              disabled={isLoading}
            >
              {t('auth.register')}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Anonymous Footer */}
      <AnonymousFooter />
    </div>
  );
};

export { Login };
